/* =====================================================================
 * authFetch — 중앙 집중 인증 fetch 래퍼
 *
 * - 현재 탭(active account) 기준 Authorization 헤더 주입
 * - 401 응답 시 HttpOnly refresh cookie로 자동 갱신 + 재시도
 * - 동시 요청 시 refresh 중복 방지 (Promise 큐잉)
 * - refresh 실패 시 현재 계정만 로그아웃
 * ===================================================================== */

import {
  getActiveAccountId,
  getAuthAccount,
  getActiveAuthAccount,
  getAuthTabId,
  getRefreshCoordinationAccountId,
  removeActiveAuthAccount,
  setActiveAccountId,
  suppressAutoRestoreForTab,
  syncFocusedAccountFromActive,
  upsertAuthAccount,
} from './authVault';
import { activeTenantKey } from '@auw/tenant';
import { dispatchAuthSessionInvalidated } from '@auw/workspace-utils';
import { isDesktop } from '@auw/workspace-utils';
import { invokeDesktop } from '@auw/workspace-utils';
import { getCloudApiBaseUrl } from '@auw/workspace-utils';

// Resolved once per module evaluation via `@auw/workspace-utils` apiRouter,
// which handles VITE_API_BASE_URL / on-prem runtime overrides and hosted
// hostname fallback (dev.askyour.ai, askyour.ai, www.askyour.ai, tauri.localhost).
// Keeping this wrapper package free of duplicate env-reading logic so all
// callers share a single source of truth for the API base URL.
const API_BASE_URL = getCloudApiBaseUrl();

/** Host injection: native broker returns only a short-lived access token. */
interface NativeSessionAdapter {
  refresh(): Promise<{ accessToken: string; expiresIn: number } | null>;
  clear(): Promise<void>;
}
let nativeSession: NativeSessionAdapter | undefined;
export function setNativeSessionAdapter(adapter?: NativeSessionAdapter): void { nativeSession = adapter; }
export function hasNativeSessionAdapter(): boolean { return Boolean(nativeSession); }
export async function clearNativeSession(): Promise<void> { await nativeSession?.clear(); }

let _cachedCsrfToken = '';

function readCachedCsrfToken(): string {
  return _cachedCsrfToken.trim();
}

function cacheCsrfToken(token: string): string {
  _cachedCsrfToken = (token || '').trim();
  return _cachedCsrfToken;
}

export function clearCachedCsrfToken(): void {
  _cachedCsrfToken = '';
  _csrfTokenPromise = null;
}

let _csrfTokenPromise: Promise<string> | null = null;
export async function ensureCsrfToken(options?: {
  forceRefresh?: boolean;
}): Promise<string> {
  const forceRefresh = options?.forceRefresh === true;
  const cachedToken = readCachedCsrfToken();
  if (!forceRefresh && cachedToken) return cachedToken;

  if (!_csrfTokenPromise || forceRefresh) {
    _csrfTokenPromise = fetch(`${API_BASE_URL}/api/v1/auth/csrf-token`, {
      credentials: 'include',
      signal: createRefreshAbortSignal(),
    })
      .then(async (res) => {
        if (!res.ok) {
          clearCachedCsrfToken();
          return '';
        }
        try {
          const data = await res.json();
          return cacheCsrfToken((data?.csrf_token as string) || '');
        } catch {
          clearCachedCsrfToken();
          return '';
        }
      })
      .catch(() => {
        clearCachedCsrfToken();
        return '';
      })
      .finally(() => { _csrfTokenPromise = null; });
  }
  const bodyToken = await _csrfTokenPromise;
  return bodyToken || readCachedCsrfToken();
}

/* ------------------------------------------------------------------ */
/*  Dev mock auth — refresh-fallback opt-out                           */
/*                                                                    */
/*  Enterprise hosts seed a synthetic access token (`dev.mock.        */
/*  access-token`) before P13's real SSO adapter ships, so engineers  */
/*  can exercise the workspace shell without a backend round-trip.    */
/*  The token is intentionally a non-JWT string so backend `/api/*`   */
/*  calls return 401 — the contract is "log into the UI shell only".  */
/*                                                                    */
/*  WITHOUT this guard the 401 path below would fall through to       */
/*  `/api/v1/auth/refresh`, which sends the host-scoped HttpOnly      */
/*  refresh cookie. On a dev machine where another origin (e.g. the   */
/*  B2C app on a sibling port) recently issued a real cookie, the     */
/*  backend happily rotates it and returns a real access token,       */
/*  silently logging the developer in as the OTHER tenant's user      */
/*  (and surfacing their workspace data inside the enterprise host).  */
/*                                                                    */
/*  Treating the mock token as "never refreshable" closes that hole   */
/*  in one place: every refresh entry-point (`doRefreshOnce`,         */
/*  `ensureFreshToken`, the `authFetch` 401 branch, the network-error */
/*  retry) consults `isDevMockAccessToken()` and short-circuits.      */
/* ------------------------------------------------------------------ */

export const DEV_MOCK_ACCESS_TOKEN = 'dev.mock.access-token';

export function isDevMockAccessToken(
  token: string | null | undefined,
): boolean {
  if (typeof token !== 'string') return false;
  return token.trim() === DEV_MOCK_ACCESS_TOKEN;
}

/* ------------------------------------------------------------------ */
/*  Token 관리 (active account 기반)                                   */
/* ------------------------------------------------------------------ */

/* Resolved lazily — see authVault.ts header for chunk-split rationale.
 * Used only inside this module; the previous `export const` had no
 * external consumers but is converted to a private getter for safety. */
let _apiKeyStorageKey: string | null = null;
function getApiKeyStorageKey(): string {
  if (_apiKeyStorageKey === null) _apiKeyStorageKey = activeTenantKey('api_key');
  return _apiKeyStorageKey;
}

function parseJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const b64 = parts[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(parts[1].length / 4) * 4, '=');
    const decoded = atob(b64);
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function getUserIdFromAccessToken(accessToken: string): string | null {
  const payload = parseJwtPayload(accessToken);
  const sub = payload?.sub;
  return typeof sub === 'string' && sub.trim() ? sub.trim() : null;
}

export function getAccessToken(): string {
  return getActiveAuthAccount()?.accessToken || '';
}

export function getApiKey(): string {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return '';
  return window.localStorage.getItem(getApiKeyStorageKey()) || '';
}

export function setAuthTokens(
  accessToken: string,
  context?: { userId?: string | null },
) {
  const normalizedAccessToken = (accessToken || '').trim();
  if (!normalizedAccessToken) return;

  const resolvedUserId = (
    context?.userId?.trim()
    || getUserIdFromAccessToken(normalizedAccessToken)
    || getActiveAccountId()
    || ''
  ).trim();
  if (!resolvedUserId) {
    throw new Error('토큰에서 user_id를 확인할 수 없습니다.');
  }

  upsertAuthAccount({
    accountId: resolvedUserId,
    userId: resolvedUserId,
    accessToken: normalizedAccessToken,
  });
  setActiveAccountId(resolvedUserId);
}

export function clearAuthTokens() {
  clearCachedCsrfToken();
  clearDesktopRefreshToken();
  removeActiveAuthAccount();
}

/**
 * 사용자가 명시적으로 로그아웃한 경우:
 * 같은 탭에서는 자동 계정 복원을 잠시 차단합니다.
 */
export function logoutActiveAccount() {
  suppressAutoRestoreForTab();
  clearAuthTokens();
}

/* ------------------------------------------------------------------ */
/*  데스크톱 refresh_token 관리 (OS Keychain 기반)                      */
/*                                                                      */
/*  Rust secure_store_* 커맨드 → macOS Keychain / Windows Credential    */
/*  Manager / Linux Secret Service 에 저장. JS localStorage 미사용.     */
/*  런타임 캐시로 sync 접근을 지원하되, 영속 저장은 OS 보안 저장소.       */
/* ------------------------------------------------------------------ */

const SECURE_KEY = 'refresh_token';
let _rtCache = '';
let _rtLoaded = false;
let _rtLoadPromise: Promise<string> | null = null;

async function loadDesktopRefreshToken(options?: {
  forceReload?: boolean;
}): Promise<string> {
  const forceReload = options?.forceReload === true;
  if (!isDesktop) return '';
  if (!forceReload && _rtLoaded) return _rtCache;
  if (_rtLoadPromise) {
    if (!forceReload) return _rtLoadPromise;
    try {
      await _rtLoadPromise;
    } catch {
      // no-op
    }
  }

  _rtLoadPromise = (async () => {
    try {
      const val = await invokeDesktop<string | null>('secure_store_get', { key: SECURE_KEY });
      _rtCache = val || '';
    } catch {
      _rtCache = '';
    }

    // Pre-launch: no legacy `auw_desktop_refresh` tokens exist in any
    // tenant's browser profile, so the historic migration path was
    // removed. If we ever ship to a browser with an older build, the
    // user simply re-authenticates — no data loss because no data existed.
    _rtLoaded = true;
    _rtLoadPromise = null;
    return _rtCache;
  })();

  return _rtLoadPromise;
}

export async function storeDesktopRefreshToken(token: string): Promise<void> {
  if (!isDesktop) return;
  _rtCache = token;
  _rtLoaded = true;
  try {
    await invokeDesktop('secure_store_set', { key: SECURE_KEY, value: token });
  } catch { /* keychain write 실패 시 런타임 캐시로 폴백 */ }
}

function clearDesktopRefreshToken(): void {
  _rtCache = '';
  _rtLoaded = true;
  if (nativeSession) {
    void nativeSession.clear().catch((err) => console.error('저장된 로그인 정보를 삭제하지 못했습니다.', err instanceof Error ? err.message : 'OS 자격 증명 저장소 오류'));
  } else if (isDesktop) {
    void invokeDesktop('secure_store_delete', { key: SECURE_KEY }).catch(() => {});
  }
}

export function hasAuthToken(): boolean {
  return Boolean(getAccessToken());
}

export function hasAuthSessionContext(): boolean {
  return Boolean(getActiveAccountId() || getAccessToken());
}

/* ------------------------------------------------------------------ */
/*  공통 헤더 생성                                                     */
/* ------------------------------------------------------------------ */

export function getHeaders(withJsonContentType = true): Record<string, string> {
  const h: Record<string, string> = {};
  if (withJsonContentType) h['Content-Type'] = 'application/json';
  const token = getAccessToken();
  if (token) h.Authorization = `Bearer ${token}`;
  const apiKey = getApiKey();
  if (apiKey) h['X-API-Key'] = apiKey;
  return h;
}

/* ------------------------------------------------------------------ */
/*  Refresh 로직 (동시 요청 시 중복 방지)                               */
/* ------------------------------------------------------------------ */

/* Resolved lazily — see authVault.ts header for chunk-split rationale. */
let _refreshLockKeyPrefix: string | null = null;
function getRefreshLockKeyPrefix(): string {
  if (_refreshLockKeyPrefix === null) _refreshLockKeyPrefix = activeTenantKey('auth_refresh_lock');
  return _refreshLockKeyPrefix;
}
let _refreshResultKeyPrefix: string | null = null;
function getRefreshResultKeyPrefix(): string {
  if (_refreshResultKeyPrefix === null) _refreshResultKeyPrefix = activeTenantKey('auth_refresh_result');
  return _refreshResultKeyPrefix;
}
const REFRESH_LOCK_TTL_MS = 15_000;
const REFRESH_WAIT_TIMEOUT_MS = 20_000;
const REFRESH_WAIT_POLL_MS = 120;
const REFRESH_RETRYABLE_STATUS = 409;
const REFRESH_ROTATION_RETRY_LIMIT = 2;
const REFRESH_ROTATION_RETRY_DELAY_MS = 800;
const REFRESH_FAILURE_BACKOFF_MS = 700;
const REFRESH_FAILURE_PEER_WAIT_MS = 2_000;
const REFRESH_RECENT_SUCCESS_WINDOW_MS = 10_000;
const REFRESH_FETCH_TIMEOUT_MS = 8_000;

interface RefreshLockState {
  ownerTabId: string;
  nonce: string;
  acquiredAt: number;
}

interface RefreshResultState {
  status: 'ok' | 'failed';
  ownerTabId: string;
  nonce: string;
  resolvedAt: number;
}

const refreshPromiseByAccount = new Map<string, Promise<boolean>>();

function getRefreshCoordinationKey(): string {
  return getRefreshCoordinationAccountId() || '__cookie__';
}

function getRefreshRetryDelayMs(res: Response): number {
  const raw = res.headers.get('X-Refresh-Retry-After-Ms');
  const parsed = raw ? Number(raw) : NaN;
  if (Number.isFinite(parsed) && parsed > 0) {
    return Math.max(100, parsed);
  }
  return REFRESH_ROTATION_RETRY_DELAY_MS;
}

function canUseLocalStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function createNonce(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function readLocalStorageJson<T>(key: string): T | null {
  if (!canUseLocalStorage()) return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeLocalStorageJson(key: string, value: unknown): void {
  if (!canUseLocalStorage()) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage quota/full 등은 refresh fallback을 막지 않도록 무시
  }
}

function removeLocalStorageItem(key: string): void {
  if (!canUseLocalStorage()) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // no-op
  }
}

function canOwnRefreshFlow(): boolean {
  if (typeof document === 'undefined') return true;
  const prerenderingDocument = document as Document & { prerendering?: boolean };
  return prerenderingDocument.prerendering !== true && document.visibilityState === 'visible';
}

function canAttemptProactiveRefresh(): boolean {
  return canOwnRefreshFlow();
}

function getRefreshLockKey(accountId: string): string {
  return `${getRefreshLockKeyPrefix()}:${accountId}`;
}

function getRefreshResultKey(accountId: string): string {
  return `${getRefreshResultKeyPrefix()}:${accountId}`;
}

function isRefreshLockAlive(lock: RefreshLockState | null): lock is RefreshLockState {
  if (!lock || !Number.isFinite(lock.acquiredAt)) return false;
  return Date.now() - lock.acquiredAt <= REFRESH_LOCK_TTL_MS;
}

function tryAcquireRefreshLock(accountId: string, tabId: string): RefreshLockState | null {
  if (!canUseLocalStorage()) {
    return {
      ownerTabId: tabId,
      nonce: createNonce(),
      acquiredAt: Date.now(),
    };
  }

  const lockKey = getRefreshLockKey(accountId);
  const existing = readLocalStorageJson<RefreshLockState>(lockKey);
  if (isRefreshLockAlive(existing) && existing.ownerTabId !== tabId) {
    return null;
  }

  const nextLock: RefreshLockState = {
    ownerTabId: tabId,
    nonce: createNonce(),
    acquiredAt: Date.now(),
  };
  writeLocalStorageJson(lockKey, nextLock);

  const confirmed = readLocalStorageJson<RefreshLockState>(lockKey);
  if (!confirmed) return null;
  if (confirmed.ownerTabId !== nextLock.ownerTabId) return null;
  if (confirmed.nonce !== nextLock.nonce) return null;
  return nextLock;
}

function releaseRefreshLock(accountId: string, lock: RefreshLockState): void {
  if (!canUseLocalStorage()) return;
  const lockKey = getRefreshLockKey(accountId);
  const current = readLocalStorageJson<RefreshLockState>(lockKey);
  if (!current) return;
  if (current.ownerTabId !== lock.ownerTabId) return;
  if (current.nonce !== lock.nonce) return;
  removeLocalStorageItem(lockKey);
}

function publishRefreshResult(
  accountId: string,
  payload: Omit<RefreshResultState, 'resolvedAt'>,
): void {
  if (!canUseLocalStorage()) return;
  writeLocalStorageJson(getRefreshResultKey(accountId), {
    ...payload,
    resolvedAt: Date.now(),
  } satisfies RefreshResultState);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function hasAccessTokenChanged(previousAccessToken: string): boolean {
  const latestToken = getAccessToken();
  return Boolean(latestToken && latestToken !== previousAccessToken);
}

async function waitForRefreshByPeer(params: {
  accountId: string;
  previousAccessToken: string;
  waitStartedAt: number;
  timeoutMs?: number;
  acceptRecentSuccessWithinMs?: number;
  stopOnPeerFailure?: boolean;
}): Promise<boolean> {
  const deadline = Date.now() + (params.timeoutMs ?? REFRESH_WAIT_TIMEOUT_MS);
  const resultKey = getRefreshResultKey(params.accountId);
  const recentSuccessThreshold = (
    typeof params.acceptRecentSuccessWithinMs === 'number'
    && params.acceptRecentSuccessWithinMs > 0
  )
    ? Date.now() - params.acceptRecentSuccessWithinMs
    : null;

  while (Date.now() < deadline) {
    if (hasAccessTokenChanged(params.previousAccessToken)) {
      return true;
    }

    const peerResult = readLocalStorageJson<RefreshResultState>(resultKey);
    if (peerResult) {
      const resolvedAfterWaitStarted = peerResult.resolvedAt >= params.waitStartedAt;
      const isRecentSuccess = (
        peerResult.status === 'ok'
        && recentSuccessThreshold !== null
        && peerResult.resolvedAt >= recentSuccessThreshold
      );
      if ((resolvedAfterWaitStarted && peerResult.status === 'ok') || isRecentSuccess) {
        return true;
      }
      if ((params.stopOnPeerFailure ?? true) && resolvedAfterWaitStarted && peerResult.status === 'failed') {
        return false;
      }
    }

    await sleep(REFRESH_WAIT_POLL_MS);
  }

  return false;
}

async function waitForPeerRefreshRecovery(params: {
  accountId: string;
  previousAccessToken: string;
}): Promise<boolean> {
  await sleep(REFRESH_FAILURE_BACKOFF_MS);
  return waitForRefreshByPeer({
    accountId: params.accountId,
    previousAccessToken: params.previousAccessToken,
    waitStartedAt: Date.now(),
    timeoutMs: REFRESH_FAILURE_PEER_WAIT_MS,
    acceptRecentSuccessWithinMs: REFRESH_RECENT_SUCCESS_WINDOW_MS,
    stopOnPeerFailure: false,
  });
}

function createRefreshAbortSignal(): AbortSignal {
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
    return AbortSignal.timeout(REFRESH_FETCH_TIMEOUT_MS);
  }
  const controller = new AbortController();
  setTimeout(() => controller.abort(), REFRESH_FETCH_TIMEOUT_MS);
  return controller.signal;
}

async function requestDesktopRefresh(): Promise<Response> {
  let refreshToken = await loadDesktopRefreshToken();
  if (!refreshToken) {
    return new Response(JSON.stringify({ detail: 'no desktop refresh token' }), {
      status: 401,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  let remainingRotationRetries = REFRESH_ROTATION_RETRY_LIMIT;

  while (true) {
    const res = await fetch(`${API_BASE_URL}/api/v1/auth/desktop-refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
      signal: createRefreshAbortSignal(),
    });

    if (res.ok) return res;

    if (res.status === REFRESH_RETRYABLE_STATUS && remainingRotationRetries > 0) {
      remainingRotationRetries -= 1;
      await sleep(getRefreshRetryDelayMs(res));
      refreshToken = await loadDesktopRefreshToken({ forceReload: true });
      if (!refreshToken) {
        clearDesktopRefreshToken();
        return new Response(JSON.stringify({ detail: 'desktop refresh token unavailable after retry' }), {
          status: 401,
          headers: { 'Content-Type': 'application/json' },
        });
      }
      continue;
    }

    if (res.status === 401) {
      clearDesktopRefreshToken();
    }
    return res;
  }
}

async function requestRefreshWithRetry(): Promise<Response> {
  if (nativeSession) {
    const session = await nativeSession.refresh();
    return new Response(JSON.stringify(session ? {
      access_token: session.accessToken, expires_in: session.expiresIn,
    } : { detail: 'no native session' }), {
      status: session ? 200 : 401, headers: { 'Content-Type': 'application/json' },
    });
  }
  if (isDesktop) return requestDesktopRefresh();

  let remainingRotationRetries = REFRESH_ROTATION_RETRY_LIMIT;
  let allowCsrfRecovery = true;
  let forceCsrfRefresh = false;

  while (true) {
    const csrfToken = await ensureCsrfToken({ forceRefresh: forceCsrfRefresh });
    forceCsrfRefresh = false;

    const res = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
      },
      credentials: 'include',
      signal: createRefreshAbortSignal(),
    });

    if (res.ok) return res;

    if (res.status === 403 && allowCsrfRecovery) {
      allowCsrfRecovery = false;
      clearCachedCsrfToken();
      forceCsrfRefresh = true;
      continue;
    }

    if (res.status === REFRESH_RETRYABLE_STATUS && remainingRotationRetries > 0) {
      remainingRotationRetries -= 1;
      await sleep(getRefreshRetryDelayMs(res));
      continue;
    }

    if (res.status === 401) {
      clearCachedCsrfToken();
    }
    return res;
  }
}

async function performRefreshWithLock(params: {
  accountId: string;
  fallbackUserId: string;
  tabId: string;
  lock: RefreshLockState;
}): Promise<boolean> {
  const { accountId, fallbackUserId, tabId, lock } = params;

  try {
    const latestAccount = getAuthAccount(accountId);
    const previousAccessToken = latestAccount?.accessToken || '';
    let res = await requestRefreshWithRetry();

    if (!res.ok && res.status === 401) {
      const peerRecovered = await waitForPeerRefreshRecovery({
        accountId,
        previousAccessToken,
      });
      if (peerRecovered) {
        res = await requestRefreshWithRetry();
      }
    }

    if (!res.ok) {
      publishRefreshResult(accountId, {
        status: 'failed',
        ownerTabId: tabId,
        nonce: lock.nonce,
      });
      return false;
    }

    const data: { access_token: string; expires_in: number; refresh_token?: string } =
      await res.json();

    if (data.refresh_token) {
      await storeDesktopRefreshToken(data.refresh_token);
    }

    const resolvedUserId = (
      latestAccount?.userId
      || getUserIdFromAccessToken(data.access_token)
      || fallbackUserId
      || ''
    ).trim();
    if (!resolvedUserId) {
      publishRefreshResult(accountId, {
        status: 'failed',
        ownerTabId: tabId,
        nonce: lock.nonce,
      });
      return false;
    }

    upsertAuthAccount({
      accountId: resolvedUserId,
      userId: resolvedUserId,
      accessToken: data.access_token,
    });
    if (getActiveAccountId() !== resolvedUserId) {
      setActiveAccountId(resolvedUserId);
    }

    publishRefreshResult(accountId, {
      status: 'ok',
      ownerTabId: tabId,
      nonce: lock.nonce,
    });
    return true;
  } catch (error) {
    publishRefreshResult(accountId, {
      status: 'failed',
      ownerTabId: tabId,
      nonce: lock.nonce,
    });
    // Native null means a rejected/missing session; an exception means the
    // server or OS credential store is unavailable. Preserve credentials on
    // that transient path instead of treating it as an authentication denial.
    if (nativeSession) throw error;
    return false;
  } finally {
    releaseRefreshLock(accountId, lock);
  }
}

async function doRefresh(): Promise<boolean> {
  const account = getActiveAuthAccount();
  const accountId = account?.accountId || getRefreshCoordinationKey();

  const tabId = getAuthTabId();
  const waitStartedAt = Date.now();
  const previousAccessToken = account?.accessToken || '';
  const canOwnRefresh = canOwnRefreshFlow();
  const lock = canOwnRefresh ? tryAcquireRefreshLock(accountId, tabId) : null;

  if (lock) {
    return performRefreshWithLock({
      accountId,
      fallbackUserId: account?.userId || '',
      tabId,
      lock,
    });
  }

  const peerRefreshed = await waitForRefreshByPeer({
    accountId,
    previousAccessToken,
    waitStartedAt,
  });
  if (peerRefreshed && hasAccessTokenChanged(previousAccessToken)) return true;
  if (!canOwnRefresh) return false;

  const retryLock = tryAcquireRefreshLock(accountId, tabId);
  if (!retryLock) return false;

  return performRefreshWithLock({
    accountId,
    fallbackUserId: account?.userId || '',
    tabId,
    lock: retryLock,
  });
}

async function doRefreshOnce(): Promise<boolean> {
  /* Dev mock guard — see DEV_MOCK_ACCESS_TOKEN block above. The mock
   * session is supposed to fail closed against the backend; firing
   * `/auth/refresh` here would coast on a sibling origin's HttpOnly
   * cookie and silently impersonate the wrong tenant's user. */
  if (isDevMockAccessToken(getAccessToken())) return false;

  const accountId = getRefreshCoordinationKey();
  const existing = refreshPromiseByAccount.get(accountId);
  if (existing) return existing;
  const task = doRefresh().finally(() => {
    refreshPromiseByAccount.delete(accountId);
  });
  refreshPromiseByAccount.set(accountId, task);
  return task;
}

export async function refreshAuthSession(): Promise<boolean> {
  return doRefreshOnce();
}

function forceLogout() {
  logoutActiveAccount();
  /* Phase 11-3a: `redirectTo` 를 API 레이어에서 뽑지 않는다. App.tsx
   * 가 tenant 컨텍스트에서 `agentLandingPath` 로 fallback 하므로
   * on-prem 테넌트가 agent 모드를 사용하지 않는 경우에도 올바른
   * 랜딩으로 흘러간다. */
  dispatchAuthSessionInvalidated({
    reason: 'auth_fetch_401',
  });
}

/* ------------------------------------------------------------------ */
/*  토큰 선제 갱신 (WebSocket 연결 전 사용)                              */
/* ------------------------------------------------------------------ */

/**
 * JWT 만료 여부를 확인하고, 만료됐거나 곧 만료될 경우 자동 갱신합니다.
 * WebSocket은 HTTP와 달리 401 재시도가 불가능하므로 연결 전에 호출해야 합니다.
 */
export async function ensureFreshToken(): Promise<string> {
  const token = getAccessToken();
  /* Dev mock guard — keep the mock token verbatim so WebSocket
   * handshake fails closed (the backend rejects it as expected),
   * instead of falling through to `/auth/refresh` and impersonating
   * a real account via a sibling origin's cookie. */
  if (token && isDevMockAccessToken(token)) return token;

  if (!token) {
    if (!canAttemptProactiveRefresh()) return '';
    const refreshedWithoutAccess = await doRefreshOnce();
    return refreshedWithoutAccess ? getAccessToken() : '';
  }

  try {
    const payload = parseJwtPayload(token);
    if (!payload) return token;
    const exp = payload.exp;
    if (typeof exp !== 'number') return token;

    const BUFFER_MS = 60_000;
    if (Date.now() < exp * 1000 - BUFFER_MS) return token;
  } catch {
    // JWT 디코딩 실패 → 갱신 시도 진행
  }

  if (!canAttemptProactiveRefresh()) return '';
  const refreshed = await doRefreshOnce();
  return refreshed ? getAccessToken() : '';
}

/* ------------------------------------------------------------------ */
/*  authFetch — 메인 래퍼                                              */
/* ------------------------------------------------------------------ */

/* Exposed as a named export *only* for the
 * ``authFetch.injectAuthHeaders.test.ts`` regression guard. Production
 * callers go through ``authFetch`` (or ``getHeaders``) — the function
 * still treats ``init`` as immutable and is safe to call ad-hoc. */
export function injectAuthHeaders(init?: RequestInit): RequestInit {
  if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
    syncFocusedAccountFromActive();
  }
  const existingHeaders = new Headers(init?.headers);
  const token = getAccessToken();
  const apiKey = getApiKey();
  /* 항상 vault 의 최신 access token 으로 ``Authorization`` 을 덮어쓴다.
   *
   * 호출자가 ``getHeaders()`` 로 Bearer 를 미리 박아 ``init.headers`` 로
   * 전달한 뒤 ``authFetch`` 가 401 → ``doRefreshOnce()`` → 재시도하는
   * 경로에서, 과거 분기 (``!existingHeaders.has('Authorization')``) 는
   * 갓 회전된 새 토큰을 무시하고 만료된 토큰을 그대로 재전송시켰다.
   * Samsung onprem ``shi-sola-agent-local-backend`` 로그상 같은 요청
   * (e.g. POST /workspace/items/.../messages) 가 refresh 200 직후
   * ExpiredSignatureError 로 다시 401 을 받던 회귀가 정확히 이 분기에서
   * 비롯됐다.
   *
   * 따라서 토큰이 비면 헤더 자체를 지워서 익명 호출(예: 로그인 전
   * health check) 도 안전하게 흐르도록 한다. */
  if (token) {
    existingHeaders.set('Authorization', `Bearer ${token}`);
  } else {
    existingHeaders.delete('Authorization');
  }
  if (apiKey && !existingHeaders.has('X-API-Key')) {
    existingHeaders.set('X-API-Key', apiKey);
  }
  return { ...init, headers: existingHeaders };
}

export async function authFetch(
  url: string,
  init?: RequestInit,
): Promise<Response> {
  let res: Response;
  const authedInit = injectAuthHeaders(init);
  const requestAuthHeader = new Headers(authedInit.headers).get('Authorization') || '';
  const requestToken = requestAuthHeader.startsWith('Bearer ')
    ? requestAuthHeader.slice('Bearer '.length).trim()
    : '';
  const refreshCoordinationKey = getRefreshCoordinationKey();
  const refreshBaselineToken = requestToken || getAccessToken();

  // Navigation can reject in-flight fetches with TypeError, not AbortError.
  // Refreshing then consumes the single-use cookie in the departing page,
  // whose replacement Set-Cookie may never reach the browser. The next page
  // is left with a revoked cookie. Only an HTTP 401 justifies auth recovery;
  // network failures propagate without rotating credentials or replaying writes.
  res = await fetch(url, authedInit);

  if (res.status === 401) {
    /* Dev mock guard — when the request token is the synthetic
     * mock, the backend's 401 is the contract, not a recoverable
     * failure. Skipping the refresh + peer-recovery + forceLogout
     * branches keeps the dev session inert: the UI gets the 401 it
     * expects, the auth vault stays as the mock-seeded record, and
     * no `dispatchAuthSessionInvalidated` fires (which would yank
     * the engineer back to the landing picker mid-flow). */
    if (isDevMockAccessToken(requestToken)) {
      return res;
    }

    // 같은 계정의 다른 탭에서 refresh를 완료했을 수 있으므로 먼저 최신 토큰으로 재시도
    if (hasAccessTokenChanged(refreshBaselineToken)) {
      const retried = await fetch(url, injectAuthHeaders(init));
      if (retried.status !== 401) {
        return retried;
      }
      res = retried;
    }

    if (!canOwnRefreshFlow()) {
      await waitForPeerRefreshRecovery({
        accountId: refreshCoordinationKey,
        previousAccessToken: refreshBaselineToken,
      });

      if (hasAccessTokenChanged(refreshBaselineToken)) {
        const retried = await fetch(url, injectAuthHeaders(init));
        if (retried.status !== 401) {
          return retried;
        }
        res = retried;
      }

      // hidden/prerender 탭은 refresh owner가 아니므로 여기서 세션을 확정 실패시키지 않는다.
      return res;
    }

    const refreshed = await doRefreshOnce();
    if (refreshed) {
      res = await fetch(url, injectAuthHeaders(init));
    } else {
      const peerRecovered = await waitForPeerRefreshRecovery({
        accountId: refreshCoordinationKey,
        previousAccessToken: refreshBaselineToken,
      });

      // refresh 실패 직전 다른 탭이 토큰을 갱신했는지 다시 확인
      if (hasAccessTokenChanged(refreshBaselineToken)) {
        const retried = await fetch(url, injectAuthHeaders(init));
        if (retried.status !== 401) {
          return retried;
        }
        res = retried;
      } else if (peerRecovered) {
        // peer 탭이 방금 refresh를 성공했으면 쿠키 동기화 후 마지막 1회만 더 시도한다.
        const lateRefresh = await doRefreshOnce();
        if (lateRefresh) {
          const retried = await fetch(url, injectAuthHeaders(init));
          if (retried.status !== 401) {
            return retried;
          }
          res = retried;
        }
      }

      if (res.status === 401) {
        if (peerRecovered) {
          return res;
        }
        forceLogout();
      }
    }
  }

  return res;
}

/* ------------------------------------------------------------------ */
/*  API_BASE_URL export (다른 클라이언트에서 재사용)                     */
/* ------------------------------------------------------------------ */

export { API_BASE_URL };
